import { UploadCloud } from "lucide-react";
import { useId, useRef, useState } from "react";
import { toast } from "sonner";
import { useMutation } from "urql";

import { graphql } from "@/gen";
import { reportError } from "@/utils/errors";

export function useImageUpload({
    onUploadFinished: onUploadFinished,
}: {
    onUploadFinished: (imageId: string) => void;
}) {
    const [isUploading, setIsUploading] = useState(false);
    const inputFile = useRef<HTMLInputElement>(null);
    const inputId = useId();

    const resetInput = () => {
        if (inputFile.current) {
            inputFile.current.value = "";
            inputFile.current.type = "text";
            inputFile.current.type = "file";
        }
    };

    const [_, generatePresignedImageUploadUrl] = useMutation(
        graphql(`
            mutation generatePresignedImageUploadUrl {
                generatePresignedImageUploadUrl {
                    uploadUrl
                    imageId
                }
            }
        `)
    );

    const [_ref, postProcessImageUpload] = useMutation(
        graphql(`
            mutation postProcessImageUpload($imageId: UUID!) {
                postProcessImageUpload(imageId: $imageId)
            }
        `)
    );

    const handleUpload = async (file: File) => {
        setIsUploading(true);

        try {
            const result = await generatePresignedImageUploadUrl({});

            const generatePresignedImageUploadUrlResponse =
                result.data?.generatePresignedImageUploadUrl;

            if (!generatePresignedImageUploadUrlResponse) {
                throw new Error("Failed to get presigned upload URL");
            }

            const response = await fetch(generatePresignedImageUploadUrlResponse.uploadUrl, {
                method: "PUT",
                headers: {
                    "Content-Type": file.type,
                },
                body: file,
            });

            if (!response.ok) {
                throw new Error("File upload failed");
            }

            await postProcessImageUpload({
                imageId: generatePresignedImageUploadUrlResponse.imageId,
            });

            toast("Image uploaded successfully", {
                icon: <UploadCloud size={18} />,
            });

            onUploadFinished(generatePresignedImageUploadUrlResponse.imageId);
            setIsUploading(false);
        } catch (error) {
            reportError(error);
        }
    };

    const startSelectFile = () => {
        if (inputFile.current) {
            inputFile.current.click();
        }
    };

    const inputProps = {
        className: "hidden",
        id: inputId,
        ref: inputFile,
        type: "file",
        accept: ".png, .jpg, .jpeg",
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files) {
                const file = e.target.files[0];
                handleUpload(file);
            }
        },
    };

    return {
        startSelectFile,
        inputProps,
        resetInput,
        isUploading,
        inputId,
    };
}
